/**
 * The v2 keyboard focus ring (redesign phase 6, #455, consistency findings
 * #15 + #18): one ring, 2px solid `--ring`, written once here. Every v2 part
 * takes its ring from this module; `focus.test.ts` fails on a hand-written one.
 *
 * Why several strings: Tailwind only sees literal class names, so the
 * selector (`focus-visible:`, `after:`, `peer-`, `group-`) has to be spelled
 * out per placement. The ring itself is the same in all of them:
 *
 *   FOCUS_RING          controls on their own (buttons, chips, links): offset 2,
 *                       outside the control.
 *   FOCUS_RING_INSET    rows and items inside a container (nav items, tree
 *                       rows, tabs triggers in a scroller, pane viewports):
 *                       offset −2, so the container never clips it.
 *   FOCUS_RING_FRAME    a ring that replaces a 1px border (inputs, textarea,
 *                       select): offset −1, drawn over the border.
 *
 * Each placement repeats the ring for the bare `data-focus` attribute (the
 * story state, Headless UI's API; apps never set it) and sets `outline-solid`,
 * because `outline-hidden` resets `--tw-outline-style` and `outline-2` alone
 * would then draw nothing (04 Disclosure note 8).
 *
 * The colour is set AT REST (`outline-ring`), not under `focus-visible:`.
 * Focus only switches width, style and offset on, which `transition-colors`
 * does not animate; so a part keeps its hover fade and the ring never fades
 * in from the text colour (finding #15). Invalid controls merge
 * `outline-destructive` over it with `cn`.
 */

/** Controls on their own: offset 2. */
export const FOCUS_RING =
  "outline-ring focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 " +
  "data-focus:outline-2 data-focus:outline-solid data-focus:outline-offset-2";

/** Rows and items inside a container: offset −2. */
export const FOCUS_RING_INSET =
  "outline-ring focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 " +
  "data-focus:outline-2 data-focus:outline-solid data-focus:-outline-offset-2";

/** Rows whose `::after` overlay covers the row (ListItem look): the ring is drawn on the overlay. */
export const FOCUS_RING_INSET_AFTER =
  "after:outline-ring focus-visible:after:outline-2 focus-visible:after:outline-solid focus-visible:after:-outline-offset-2 " +
  "data-focus:after:outline-2 data-focus:after:outline-solid data-focus:after:-outline-offset-2";

/** A ring over a 1px border, on the element itself: offset −1. */
export const FOCUS_RING_FRAME =
  "outline-ring focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-1 " +
  "data-focus:outline-2 data-focus:outline-solid data-focus:-outline-offset-1";

/** The same on a frame element drawn after its `peer` control (Input, DateRangePicker). */
export const FOCUS_RING_FRAME_PEER =
  "outline-ring peer-focus-visible:outline-2 peer-focus-visible:outline-solid peer-focus-visible:-outline-offset-1 " +
  "peer-data-focus:outline-2 peer-data-focus:outline-solid peer-data-focus:-outline-offset-1";

/** On an inner indicator, driven by its `group` (a tab's label box, the resize grip): offset 2. */
export const FOCUS_RING_GROUP =
  "outline-ring group-focus-visible:outline-2 group-focus-visible:outline-solid group-focus-visible:outline-offset-2 " +
  "group-data-focus:outline-2 group-data-focus:outline-solid group-data-focus:outline-offset-2";
